import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "~/components/ui/toaster";
import { type AdventureMap, deleteMap } from "~/lib/api";
import { routes } from "~/lib/routes";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import ShareDialog from "~/components/ShareDialog";
import { MapListItem } from "./MapListItem";

interface MapListProps {
  maps: AdventureMap[];
  loading: boolean;
  error: string;
  onMapsChange: (maps: AdventureMap[]) => void;
}

export default function MapList({ maps, loading, error, onMapsChange }: MapListProps) {
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [shareMap, setShareMap] = useState<AdventureMap | null>(null);

  const handleShareLinkCreated = (shareId: string) => {
    if (!shareMap) return;
    const updated = { ...shareMap, shareId };
    setShareMap(updated);
    onMapsChange(maps.map((m) => (m.id === shareMap.id ? updated : m)));
  };

  const handleDelete = async (mapId: string, mapName: string) => {
    if (!confirm(`Are you sure you want to delete "${mapName}"? This cannot be undone.`)) {
      return;
    }

    try {
      setDeletingId(mapId);
      await deleteMap(mapId);
      onMapsChange(maps.filter((m) => m.id !== mapId));
      toast.success("Map deleted successfully");
    } catch (err) {
      console.error("Failed to delete map:", err);
      toast.error("Failed to delete map. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  if (error) {
    return (
      <Alert variant="destructive" className="mb-6">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="text-lg text-muted-foreground">Loading your maps...</p>
      </div>
    );
  }

  if (maps.length === 0) {
    return (
      <div className="text-center py-12">
        <h3 className="text-xl font-semibold text-foreground mb-2">No maps yet</h3>
        <p className="text-muted-foreground mb-6 leading-relaxed">Create your first adventure map to get started!</p>
        <Button asChild size="lg">
          <Link to={routes.to.maps.new()}>Create Your First Map</Link>
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {maps.map((map) => (
          <MapListItem key={map.id} map={map} deletingId={deletingId} onDelete={handleDelete} onShare={setShareMap} />
        ))}
      </div>

      {shareMap && (
        <ShareDialog
          map={shareMap}
          open
          onOpenChange={(open) => !open && setShareMap(null)}
          onShareLinkCreated={handleShareLinkCreated}
        />
      )}
    </>
  );
}
